/**
 * The pure resolver, with the ID generator passed in — so it has no dependency on the browser's Firestore
 * client and can run in Node too (the e2e suite seeds the same catalogue with the Admin SDK).
 * `resolvePlaceholders.ts` is the app's own entry, with `generateFirestoreId` bound.
 */
export interface IResolvePlaceholdersContext {
  accountID: string
  userID: string
}

/**
 * Resolves `{{token}}` placeholders in a seed JSON array string and returns
 * the parsed, cleaned array of plain objects ready to write to Firestore.
 *
 * Token types:
 * - `{{accountID}}`       — replaced with `context.accountID`
 * - `{{userID}}`          — replaced with `context.userID`
 * - `{{now}}`             — replaced with `Date.now()` as a number
 * - `{{currentYear}}`     — replaced with the four-digit year of the seed run.
 *                           The only token that substitutes **inline**, so a
 *                           business date can be written as
 *                           `"{{currentYear}}-12-24"` and stay in the current
 *                           year on every run instead of rotting.
 * - `{{generated}}`       — replaced with a unique Firestore ID per occurrence
 * - `{{generated:name}}`  — replaced with a stable Firestore ID per unique name within a run
 * - `{{anyOtherToken}}`   — replaced with a stable Firestore ID per unique token string within a run
 *                           (enables cross-file foreign keys: `{{at-bly}}` in analysis-tests.json
 *                            and in analyses.json both resolve to the same ID)
 *
 * **`_seedID` entries** — objects with a `_seedID` field register their ID in the shared `idMap`
 * under that seed ID. The `id: "{{generated}}"` on such objects resolves to the registered ID
 * rather than a random one, so other files can reference it by name (e.g. `{{at-bly}}`).
 *
 * The `_seedID` and `_comment` fields are stripped from all output objects.
 *
 * @param rawJson  — raw JSON string from a seed file (must be a JSON array)
 * @param context  — account and user IDs for context-specific tokens
 * @param idMap    — shared mutable ID map; pass the same object across all seed file calls
 *                   so named tokens resolve consistently across files. Defaults to `{}`.
 */
export function resolvePlaceholdersWith(
  generateFirestoreId: () => string,
  rawJson: string,
  context: IResolvePlaceholdersContext,
  idMap: Record<string, string> = {}
): Record<string, unknown>[] {
  const now = Date.now()
  const currentYear = new Date().getFullYear()

  // ── Pass 1: register every _seedID in the idMap before any resolution ──────
  // This ensures forward references work: if file A references {{at-bly}} but
  // analysis-tests.json (which owns at-bly) is resolved later, the ID is stable.
  const seedIDPattern = /"_seedID"\s*:\s*"([^"]+)"/g
  let seedIDMatch: RegExpExecArray | null
  while ((seedIDMatch = seedIDPattern.exec(rawJson)) !== null) {
    const seedID = seedIDMatch[1]
    if (seedID === undefined) continue
    if (!(seedID in idMap)) {
      idMap[seedID] = generateFirestoreId()
    }
  }

  // ── Parse ──────────────────────────────────────────────────────────────────
  const items = JSON.parse(rawJson) as Record<string, unknown>[]

  // ── Helpers ────────────────────────────────────────────────────────────────

  function isToken(s: string): boolean {
    return /^\{\{[^}]+\}\}$/.test(s)
  }

  function resolveToken(token: string): string | number {
    const m = token.match(/^\{\{([^}]+)\}\}$/)
    if (!m) return token // not a token — return unchanged

    const name = m[1]
    if (name === undefined) return token

    if (name === 'accountID') return context.accountID
    if (name === 'userID') return context.userID
    if (name === 'now') return now
    if (name === 'generated') return generateFirestoreId() // always unique per call

    // Named token (including "generated:some-name") — stable within this run
    if (!(name in idMap)) {
      idMap[name] = generateFirestoreId()
    }
    return idMap[name] ?? token
  }

  function resolveValue(val: unknown): unknown {
    if (typeof val === 'string') {
      // `{{currentYear}}` substitutes inline — every other token has to be the
      // whole string, since `isToken` anchors. Done first so a date built from
      // it is a plain string by the time the whole-string check runs, and so it
      // never falls through to the catch-all that turns an unknown token into a
      // Firestore ID (which would silently corrupt the date).
      const resolved = val.replace(
        /\{\{currentYear\}\}/g,
        String(currentYear)
      )
      return isToken(resolved) ? resolveToken(resolved) : resolved
    }
    if (Array.isArray(val)) {
      return val.map((item) => resolveValue(item))
    }
    if (val !== null && typeof val === 'object') {
      return resolveObject(val as Record<string, unknown>)
    }
    return val
  }

  function resolveObject(
    obj: Record<string, unknown>
  ): Record<string, unknown> {
    const seedID =
      typeof obj['_seedID'] === 'string' ? obj['_seedID'] : undefined
    const result: Record<string, unknown> = {}

    for (const [key, value] of Object.entries(obj)) {
      // Strip internal seed-only fields from the output
      if (key === '_seedID' || key === '_comment') continue

      // Resolve object key if it is itself a token (e.g. "{{generated:tli-alger}}" used as a map key)
      const resolvedKey = isToken(key) ? String(resolveToken(key)) : key

      // Special case: the `id` field on a _seedID entry must use the pre-registered
      // seed ID rather than generating a fresh random ID, so cross-file references
      // (e.g. {{at-bly}} in analyses.json) resolve to the same document ID.
      if (
        resolvedKey === 'id' &&
        typeof value === 'string' &&
        value === '{{generated}}' &&
        seedID
      ) {
        result['id'] = idMap[seedID] // already registered in pass 1
        continue
      }

      result[resolvedKey] = resolveValue(value)
    }

    return result
  }

  // ── Pass 2: resolve all items ───────────────────────────────────────────────
  // Drop comment-only sentinel objects (e.g. { "_comment": "--- SECTION ---" })
  // which have no meaningful fields after stripping internal keys.
  return items.map((item) => resolveObject(item)).filter((item) => 'id' in item)
}

